import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { iniciarTrabajoOt } from '../../api/client'
import PlanificarAsignacion from './PlanificarAsignacion'
import { hora } from '../capsulas/tiempo'

// Putting a person to work on an OT, from anywhere (the assignment board, the
// OT's panel): asignar(persona, ot). The server's rules decide:
//   * only a planned OT takes people -- one in execution without its planning
//     (loaded from monday) asks for it here and is planned with the assignment
//     (same rules as admission; function "planificar");
//   * outside working hours only as authorized overtime -- asked, then retried.
// What happened shows in a short notice. Render `ui` wherever the hook is
// used. persona: { id, nombre }; ot: { id, codigo, prioridad, fecha_compromiso,
// grupo_id | grupo, responsable_id | responsable, horas_estimadas }.
export function useAsignarOt({ onHecho } = {}) {
  const { t } = useTranslation('operations')
  const [extra, setExtra] = useState(null)          // { persona, ot, planificacion, jornada }
  const [plan, setPlan] = useState(null)            // { persona, ot, faltan, error }
  const [ocupado, setOcupado] = useState(false)
  const [aviso, setAviso] = useState(null)          // { texto, error }

  useEffect(() => {
    if (!aviso) return
    const id = setTimeout(() => setAviso(null), aviso.error ? 7000 : 5000)
    return () => clearTimeout(id)
  }, [aviso])

  const intentar = async (persona, ot, { autorizar = false, planificacion = null } = {}) => {
    setOcupado(true)
    try {
      const r = await iniciarTrabajoOt(ot.id, {
        persona_id: persona.id, autorizar_hora_extra: autorizar, ...(planificacion ? { planificacion } : {}),
      })
      setExtra(null)
      setPlan(null)
      const base = r.pausado
        ? t('ot.asignar.aviso.pausando', { persona: persona.nombre, ot: ot.codigo, previo: r.pausado })
        : t('ot.asignar.aviso.asignada', { persona: persona.nombre, ot: ot.codigo })
      setAviso({ texto: base + (r.trabajo.hora_extra ? ` ${t('ot.asignar.aviso.extra')}` : '') + (planificacion ? ` ${t('ot.asignar.aviso.planificada')}` : '') })
      onHecho?.(r)
    } catch (err) {
      const d = err.response?.data?.detail
      if (d?.code === 'fuera_de_horario') { setPlan(null); setExtra({ persona, ot, planificacion, jornada: d.jornada }) }
      else if (d?.code === 'falta_planificacion') setPlan({ persona, ot, faltan: d.faltan || [] })
      else if (planificacion && d?.code === 'validacion') setPlan({ persona, ot, faltan: d.faltan || [], error: true })
      else if (planificacion && d?.code === 'sin_funcion') { setPlan(null); setAviso({ texto: t('ot.asignar.sinPlanificar'), error: true }) }
      else setAviso({ texto: d?.message || t('ot.panel.fallo'), error: true })
    }
    setOcupado(false)
  }

  const ui = (
    <>
      {plan && (
        <PlanificarAsignacion key={`${plan.ot.id}-${plan.persona.id}`} {...plan} ocupado={ocupado} t={t}
          onClose={() => setPlan(null)}
          onConfirmar={planificacion => intentar(plan.persona, plan.ot, { planificacion })} />
      )}
      {extra && (
        <Modal title={t('ot.asignar.extra.titulo')} onClose={() => setExtra(null)}>
          <div className="space-y-4 text-sm">
            <p className="text-gray-700">
              {extra.jornada
                ? t('ot.asignar.extra.fuera', { inicio: hora(extra.jornada.inicio), fin: hora(extra.jornada.fin) })
                : t('ot.asignar.extra.noLaboral')}
            </p>
            <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900">
              {t('ot.asignar.extra.pregunta', { persona: extra.persona.nombre, ot: extra.ot.codigo })}
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setExtra(null)}
                className="bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
                {t('ot.asignar.cancelar')}
              </button>
              <button type="button" disabled={ocupado}
                onClick={() => intentar(extra.persona, extra.ot, { autorizar: true, planificacion: extra.planificacion })}
                className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
                {t('ot.asignar.extra.autorizar')}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {aviso && (
        <div role="status" className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl shadow-lg px-4 py-3 text-sm ${
          aviso.error ? 'bg-red-600 text-white' : 'bg-gray-900 text-white'}`}>
          {aviso.texto}
        </div>
      )}
    </>
  )

  return { asignar: (persona, ot) => intentar(persona, ot), ocupado, ui }
}
